import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, MagnifyingGlass, ShoppingCart, Trash, Minus, Storefront, Warning, ClockCountdown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { createOrder, getOrderPickerInventory, validateOrderDiscount } from '../lib/api';
import { useCompany } from '../context/CompanyContext';
import UomPicker from './UomPicker';

/**
 * Walk-in / manual order builder.
 * Two-pane modal:
 *   LEFT  — customer details + cart
 *   RIGHT — searchable inventory picker with on-hand / available counts
 *
 * Click a stock row to add it to cart (qty 1, increments on repeat click).
 * Submit POSTs to /api/orders with channel='manual'.
 */
export default function ManualOrderDialog({ trigger, onCreated }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const searchRef = React.useRef(null);
  // Tenant-level toggle: when false, the backend rejects orders with
  // insufficient stock (422 + short_lines). We mirror that here so users
  // see the constraint up-front instead of after building a cart.
  const { company } = useCompany();
  const allowBackorders = company?.allow_backorders !== false;

  const [customer, setCustomer] = useState({
    customer_name: '',
    customer_email: '',
    shipping_address: '',
    notes: '',
    is_walkin: true,
  });
  const [cart, setCart] = useState([]); // {variant_id, sku_code, sku_name, unit_price, available, quantity}
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [inventory, setInventory] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountState, setDiscountState] = useState({ status: 'idle', payload: null, error: null });

  // Debounce the search input by ~250ms so we don't spam the picker endpoint
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadInventory = useCallback(async (q) => {
    setInvLoading(true);
    try {
      const res = await getOrderPickerInventory({ q, limit: 60 });
      setInventory(res.data.rows || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load inventory');
    } finally {
      setInvLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadInventory(searchDebounced);
  }, [open, searchDebounced, loadInventory]);

  const reset = () => {
    setCustomer({ customer_name: '', customer_email: '', shipping_address: '', notes: '', is_walkin: true });
    setCart([]);
    setSearch('');
    setDiscountCode('');
    setDiscountState({ status: 'idle', payload: null, error: null });
  };

  const addToCart = (row) => {
    // Only block if the tenant has backorders disabled AND there's no stock.
    // When backorders are allowed (default) we let the user add OOS items
    // and the backend will mark them as backordered on submit.
    if (row.available <= 0 && !allowBackorders) {
      toast.error(`${row.sku_code} is out of stock and backorders are disabled for this tenant`);
      return;
    }
    setCart((prev) => {
      const existing = prev.find((c) => c.variant_id === row.variant_id);
      if (existing) {
        const next = existing.quantity + 1;
        // Block over-allocating only when backorders are off — otherwise
        // surplus qty over `available` becomes the backorder portion.
        if (!allowBackorders && next > row.available) {
          toast.error(`Only ${row.available} on hand for ${row.sku_code} (backorders disabled)`);
          return prev;
        }
        return prev.map((c) => c.variant_id === row.variant_id ? { ...c, quantity: next } : c);
      }
      return [...prev, {
        variant_id: row.variant_id,
        sku_code: row.sku_code,
        sku_name: row.sku_name,
        unit_price: row.unit_price,
        // iter164c — store the original base price so toggling UOM back to
        // base restores the correct unit_price (we mutate `unit_price` for
        // live cart math when an alternate UOM is picked).
        base_unit_price: row.unit_price,
        available: row.available,
        quantity: 1,
        // iter149 — fractional qty + UoM (passed through from the SKU
        // picker row so the cart can render the correct input shape).
        allow_fractional: !!row.allow_fractional,
        unit_of_measure: row.unit_of_measure || 'each',
        // iter164 — multi-UOM conversions for the per-line UOM picker
        uom_conversions: row.uom_conversions || [],
        // Current UOM for this cart line — defaults to the SKU base
        uom: (row.unit_of_measure || 'each').toLowerCase(),
      }];
    });
  };

  const updateQty = (variant_id, delta) => {
    setCart((prev) => prev
      .map((c) => {
        if (c.variant_id !== variant_id) return c;
        const step = c.allow_fractional ? 0.5 : 1;
        const next = Math.max(0, Math.round((c.quantity + delta * step) * 100) / 100);
        if (!allowBackorders && next > c.available) {
          toast.error(`Only ${c.available} on hand for ${c.sku_code} (backorders disabled)`);
          return c;
        }
        return { ...c, quantity: next };
      })
      .filter((c) => c.quantity > 0));
  };

  // iter149 — set exact qty (used by the fractional-input cart row)
  const setExactQty = (variant_id, raw) => {
    setCart((prev) => prev.map((c) => {
      if (c.variant_id !== variant_id) return c;
      const fractional = !!c.allow_fractional;
      if (raw === '' || raw === null) return { ...c, quantity: 0 };
      const parsed = fractional ? parseFloat(raw) : parseInt(raw, 10);
      if (Number.isNaN(parsed) || parsed < 0) return c;
      const clean = fractional ? Math.round(parsed * 100) / 100 : parsed;
      if (!allowBackorders && clean > c.available) {
        toast.error(`Only ${c.available} on hand for ${c.sku_code} (backorders disabled)`);
        return c;
      }
      return { ...c, quantity: clean };
    }));
  };

  const removeFromCart = (variant_id) => {
    setCart((prev) => prev.filter((c) => c.variant_id !== variant_id));
  };

  // iter164 — Set the per-line UOM for the cart row. Backend converts to base
  // units at order create time; we keep the operator-entered UOM + qty on the
  // line for invoice/picking display.
  // iter164c — Also resolve the per-UOM unit price so the cart total reflects
  // "$5/L × 2 L = $10" live. Falls back to (base price × factor) when the
  // SKU has no explicit per-UOM price.
  const setLineUom = (variant_id, code) => {
    setCart((prev) => prev.map((c) => {
      if (c.variant_id !== variant_id) return c;
      const base = (c.unit_of_measure || 'each').toLowerCase();
      const convs = c.uom_conversions || [];
      const conv = convs.find((r) => (r.code || '').toLowerCase() === code);
      let unit_price = c.unit_price;
      if (code === base) {
        unit_price = c.base_unit_price ?? c.unit_price;
      } else if (conv) {
        if (conv.unit_price != null) unit_price = Number(conv.unit_price);
        else if (c.base_unit_price != null) unit_price = Number(c.base_unit_price) * (Number(conv.factor) || 1);
      }
      return { ...c, uom: code, unit_price };
    }));
  };

  // Operator can record a supplier-promised arrival date so the customer
  // notification email reads e.g. "expected ship 2026-03-15". Stored on the
  // cart row as ISO YYYY-MM-DD; only sent on submit when the line still
  // has a backorder portion.
  const setLineEta = (variant_id, eta) => {
    setCart((prev) => prev.map((c) =>
      c.variant_id === variant_id ? { ...c, eta: eta || '' } : c));
  };

  const totals = cart.reduce(
    (acc, c) => {
      const fulfilled = Math.min(c.quantity, Math.max(0, c.available));
      const backorder = Math.max(0, c.quantity - Math.max(0, c.available));
      return {
        qty: acc.qty + c.quantity,
        revenue: acc.revenue + c.quantity * (c.unit_price || 0),
        lines: acc.lines + 1,
        fulfilled_qty: acc.fulfilled_qty + fulfilled,
        backorder_qty: acc.backorder_qty + backorder,
        backorder_lines: acc.backorder_lines + (backorder > 0 ? 1 : 0),
      };
    },
    { qty: 0, revenue: 0, lines: 0, fulfilled_qty: 0, backorder_qty: 0, backorder_lines: 0 }
  );

  // iter154 — re-validate the discount code whenever the cart subtotal or
  // customer email changes (debounced). The validate call is cheap and the
  // backend re-computes the final discount on order create — this is just
  // for live UI feedback.
  useEffect(() => {
    const code = (discountCode || '').trim().toUpperCase();
    if (!code || cart.length === 0) {
      setDiscountState({ status: 'idle', payload: null, error: null });
      return;
    }
    setDiscountState((s) => ({ ...s, status: 'checking' }));
    const t = setTimeout(async () => {
      try {
        const r = await validateOrderDiscount({
          code,
          subtotal: totals.revenue,
          customer_email: customer.customer_email || null,
          sku_ids: [],   // we don't have sku_ids from cart yet (cart stores variant); restrictions still enforced on backend
        });
        if (r.data?.valid) setDiscountState({ status: 'valid', payload: r.data, error: null });
        else setDiscountState({ status: 'invalid', payload: null, error: r.data?.reason || 'invalid' });
      } catch (e) {
        setDiscountState({ status: 'invalid', payload: null, error: 'check_failed' });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [discountCode, totals.revenue, customer.customer_email, cart.length]);

  const appliedDiscount = discountState.status === 'valid' ? (discountState.payload?.discount_amount || 0) : 0;
  const netTotal = Math.max(0, totals.revenue - appliedDiscount);

  const canSubmit = !submitting
    && customer.customer_name.trim()
    && (customer.is_walkin || customer.shipping_address.trim())
    && cart.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        channel: 'manual',
        customer_name: customer.customer_name.trim(),
        customer_email: customer.customer_email.trim() || null,
        // For walk-in, server still requires a shipping address — use a sentinel
        shipping_address: customer.is_walkin
          ? (customer.shipping_address.trim() || 'WALK-IN · counter pickup')
          : customer.shipping_address.trim(),
        notes: customer.notes.trim() || (customer.is_walkin ? 'Walk-in counter sale' : ''),
        items: cart.map((c) => {
          const isBackorder = c.quantity > Math.max(0, c.available);
          const item = {
            variant_id: c.variant_id,
            sku_code: c.sku_code,
            sku_name: c.sku_name,
            quantity: c.quantity,
            unit_price: c.unit_price,
          };
          // iter164 — only send uom when operator picked a non-base UOM
          const baseUom = (c.unit_of_measure || 'each').toLowerCase();
          if (c.uom && c.uom !== baseUom) item.uom = c.uom;
          // Only attach eta when the line is actually backordered — backend
          // drops it from fully-fulfilled lines anyway, but no point sending
          // bytes that won't be stored.
          if (isBackorder && c.eta) item.eta = c.eta;
          return item;
        }),
      };
      // Attach validated discount code + subtotal so backend can re-validate
      // and atomically redeem it.
      if (discountState.status === 'valid' && discountCode.trim()) {
        body.discount_code = discountCode.trim().toUpperCase();
        body.subtotal = totals.revenue;
      }
      const res = await createOrder(body);
      const status = res.data?.status;
      const bo = res.data?.backorder_summary;
      if (bo && bo.qty_short > 0) {
        toast.success(
          `Order ${res.data?.order_number || 'created'} · ${totals.qty} units · $${totals.revenue.toFixed(2)} — ${bo.qty_short} unit(s) on backorder across ${bo.lines_short} line(s)`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Order ${res.data?.order_number || 'created'} · ${totals.qty} units · $${totals.revenue.toFixed(2)}${status ? ` · ${status}` : ''}`);
      }
      setOpen(false);
      reset();
      onCreated?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Order creation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="create-order-btn" className="bg-brand hover:bg-brand/90 text-brand-foreground">
            <Plus size={18} className="mr-1" /> Manual Order
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        data-testid="manual-order-dialog"
        className="border border-border max-w-5xl w-[95vw] p-0 overflow-hidden"
        onOpenAutoFocus={(e) => { e.preventDefault(); setTimeout(() => searchRef.current?.focus(), 30); }}
      >
        <DialogHeader className="p-4 border-b border-border">
          <DialogTitle className="flex items-center gap-2">
            <Storefront size={18} weight="bold" className="text-brand" />
            New Walk-in / Manual Order
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Pick stock from the right panel — inventory reservations apply on submit. Out-of-stock or short lines are accepted as <strong>backorders</strong> and ship automatically when receiving fills the gap. Use this for counter sales, phone orders, or any order placed outside the integrated channels.
          </p>
          {!allowBackorders && (
            <div data-testid="manual-order-backorders-disabled" className="mt-2 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1.5">
              <Warning size={12} weight="bold" className="mt-0.5 shrink-0" />
              <span>Backorders are <strong>disabled</strong> for your tenant — only on-hand stock can be added. Owners can enable them in <span className="font-mono">Settings → Backorders</span>.</span>
            </div>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr] gap-0 max-h-[75vh]">
          {/* LEFT — Customer + Cart */}
          <div className="border-r border-border p-4 overflow-y-auto">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Customer</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={customer.is_walkin}
                  onChange={(e) => setCustomer({ ...customer, is_walkin: e.target.checked })}
                  data-testid="manual-order-walkin"
                  className="rounded border-input"
                />
                <span className="font-medium text-foreground">Walk-in customer (counter pickup)</span>
              </label>
              <div>
                <Label className="text-xs">Customer Name *</Label>
                <Input
                  value={customer.customer_name}
                  onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })}
                  placeholder={customer.is_walkin ? 'e.g. Counter sale · cash' : 'Jane Doe'}
                  data-testid="manual-order-customer-name"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Email (optional)</Label>
                <Input
                  type="email"
                  value={customer.customer_email}
                  onChange={(e) => setCustomer({ ...customer, customer_email: e.target.value })}
                  placeholder="receipt@example.com"
                  data-testid="manual-order-customer-email"
                  className="mt-1"
                />
              </div>
              {!customer.is_walkin && (
                <div>
                  <Label className="text-xs">Shipping Address *</Label>
                  <Input
                    value={customer.shipping_address}
                    onChange={(e) => setCustomer({ ...customer, shipping_address: e.target.value })}
                    placeholder="123 Main St, Sydney NSW 2000"
                    data-testid="manual-order-shipping-address"
                    required={!customer.is_walkin}
                    className="mt-1"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs">Notes</Label>
                <Input
                  value={customer.notes}
                  onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                  placeholder="Optional internal note"
                  data-testid="manual-order-notes"
                  className="mt-1"
                />
              </div>
            </div>

            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-6 mb-3 flex items-center gap-2">
              <ShoppingCart size={14} weight="bold" /> Cart
              {cart.length > 0 && (
                <span data-testid="manual-order-cart-count" className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded-full bg-brand text-brand-foreground">
                  {totals.lines} line · {totals.qty} units
                </span>
              )}
            </h3>
            {cart.length === 0 ? (
              <div data-testid="manual-order-cart-empty" className="text-xs text-muted-foreground italic text-center py-8 border border-dashed border-border rounded-md">
                No items yet — pick stock on the right →
              </div>
            ) : (
              <div className="space-y-2" data-testid="manual-order-cart">
                {cart.map((c) => {
                  const fulfilled = Math.min(c.quantity, Math.max(0, c.available));
                  const backorder = Math.max(0, c.quantity - Math.max(0, c.available));
                  return (
                  <div
                    key={c.variant_id}
                    data-testid={`manual-order-cart-row-${c.variant_id}`}
                    className={`bg-card border rounded-md p-2 ${
                      backorder > 0 ? 'border-amber-300 bg-amber-50/40' : 'border-border'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[11px] font-semibold text-foreground truncate">{c.sku_code}</span>
                          {backorder > 0 && (
                            <span
                              data-testid={`manual-order-cart-backorder-badge-${c.variant_id}`}
                              className="text-[9px] inline-flex items-center gap-0.5 text-amber-800 bg-amber-100 border border-amber-200 px-1 py-0.5 font-bold uppercase tracking-wider"
                              title={`${backorder} unit(s) will be placed on backorder and ship when stock arrives`}
                            >
                              <ClockCountdown size={10} weight="bold" /> Backorder × {backorder}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">{c.sku_name}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">
                          ${(c.unit_price || 0).toFixed(2)} · on-hand {c.available}
                          {backorder > 0 && (
                            <> · <span className="text-emerald-700">{fulfilled} ship now</span> · <span className="text-amber-700">{backorder} on backorder</span></>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => updateQty(c.variant_id, -1)}
                          className="w-6 h-6 flex items-center justify-center rounded-md border border-border hover:bg-accent text-muted-foreground"
                          data-testid={`manual-order-qty-dec-${c.variant_id}`}
                        >
                          <Minus size={12} />
                        </button>
                        {c.allow_fractional ? (
                          <input
                            type="number"
                            step={0.01}
                            min={0}
                            value={c.quantity}
                            onChange={(e) => setExactQty(c.variant_id, e.target.value)}
                            data-testid={`manual-order-qty-input-${c.variant_id}`}
                            className="w-20 px-1 py-0.5 text-sm font-mono font-semibold border border-border rounded text-center tabular-nums"
                            title={`Fractional qty allowed (${c.unit_of_measure})`}
                          />
                        ) : (
                          <span className="text-sm font-mono font-semibold w-7 text-center" data-testid={`manual-order-qty-${c.variant_id}`}>{c.quantity}</span>
                        )}
                        {c.allow_fractional && c.unit_of_measure && c.unit_of_measure !== 'each' && (
                          <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground" data-testid={`manual-order-qty-uom-${c.variant_id}`}>{c.unit_of_measure}</span>
                        )}
                        {/* iter164 — per-line UOM picker (rendered only when SKU has alternates configured) */}
                        {(c.uom_conversions && c.uom_conversions.length > 0) && (
                          <UomPicker
                            sku={{
                              unit_of_measure: c.unit_of_measure,
                              uom_conversions: c.uom_conversions,
                            }}
                            value={c.uom || (c.unit_of_measure || '').toLowerCase()}
                            onChange={(code) => setLineUom(c.variant_id, code)}
                            testId={`manual-order-uom-${c.variant_id}`}
                            className="h-7 text-[10px]"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => updateQty(c.variant_id, +1)}
                          className="w-6 h-6 flex items-center justify-center rounded-md border border-border hover:bg-accent text-muted-foreground"
                          data-testid={`manual-order-qty-inc-${c.variant_id}`}
                        >
                          <Plus size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFromCart(c.variant_id)}
                          className="ml-1 text-rose-500 hover:text-rose-700"
                          data-testid={`manual-order-remove-${c.variant_id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                    {backorder > 0 && (
                      <div className="mt-2 pt-2 border-t border-amber-200 flex items-center gap-2 text-[11px]">
                        <ClockCountdown size={12} weight="bold" className="text-amber-700 shrink-0" />
                        <Label htmlFor={`eta-${c.variant_id}`} className="text-amber-800 font-semibold uppercase tracking-widest text-[10px] shrink-0">
                          Backorder ETA
                        </Label>
                        <Input
                          id={`eta-${c.variant_id}`}
                          type="date"
                          value={c.eta || ''}
                          min={new Date().toISOString().slice(0, 10)}
                          onChange={(e) => setLineEta(c.variant_id, e.target.value)}
                          data-testid={`manual-order-eta-${c.variant_id}`}
                          className="h-7 text-[11px] py-0.5 px-2 max-w-[160px]"
                        />
                        {c.eta && (
                          <button
                            type="button"
                            onClick={() => setLineEta(c.variant_id, '')}
                            data-testid={`manual-order-eta-clear-${c.variant_id}`}
                            className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
                            title="Clear ETA"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
                <div data-testid="manual-order-totals" className="space-y-1 pt-2 border-t border-border text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-mono font-semibold text-foreground text-base">
                      ${totals.revenue.toFixed(2)}
                    </span>
                  </div>
                  {totals.backorder_qty > 0 && (
                    <div data-testid="manual-order-backorder-summary" className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5 mt-2">
                      <ClockCountdown size={12} weight="bold" className="mt-0.5 shrink-0" />
                      <div>
                        <strong>{totals.backorder_qty}</strong> unit(s) across <strong>{totals.backorder_lines}</strong> line(s) will be placed on <strong>backorder</strong>.
                        Order stays open and the backorder portion auto-ships when receiving fills the stock.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Discount code (iter154 — per-tenant order discounts) */}
            {cart.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border" data-testid="manual-order-discount-wrap">
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Discount code <span className="font-normal lowercase tracking-normal">(optional)</span></Label>
                <Input
                  data-testid="manual-order-discount-input"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  placeholder="SUMMER10"
                  className="font-mono uppercase mt-1"
                  autoComplete="off"
                />
                {discountState.status === 'checking' && (
                  <div className="text-[11px] text-muted-foreground mt-1" data-testid="manual-order-discount-checking">Checking…</div>
                )}
                {discountState.status === 'valid' && (
                  <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 mt-1 flex items-center justify-between" data-testid="manual-order-discount-valid">
                    <span>✓ {discountState.payload?.label} applied</span>
                    <span className="font-bold tabular-nums">−${appliedDiscount.toFixed(2)}</span>
                  </div>
                )}
                {discountState.status === 'invalid' && (
                  <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 mt-1" data-testid="manual-order-discount-invalid">
                    {(() => {
                      const r = discountState.error;
                      if (r === 'expired')           return 'This code has expired.';
                      if (r === 'not_yet')           return 'This code is not yet active.';
                      if (r === 'exhausted')         return 'This code has reached its redemption limit.';
                      if (r === 'disabled')          return 'This code has been disabled.';
                      if (r === 'below_minimum')     return `Add more items — code requires $${discountState.payload?.min_order_amount || '?'}+ subtotal.`;
                      if (r === 'sku_not_eligible')  return 'No items in cart qualify for this code.';
                      if (r === 'customer_not_eligible') return 'This customer is not eligible for this code.';
                      if (r === 'tier_not_eligible') return 'Customer price-list tier is not eligible.';
                      if (r === 'already_used')      return 'This customer has already used this code.';
                      if (r === 'not_found')         return 'Code not found — double-check spelling.';
                      return 'Code not valid.';
                    })()}
                  </div>
                )}
                {appliedDiscount > 0 && (
                  <div className="mt-2 flex items-center justify-between text-xs font-bold border-t border-border pt-2" data-testid="manual-order-net-total">
                    <span>Net total</span>
                    <span className="tabular-nums text-brand">${netTotal.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-border">
              <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="manual-order-submit"
                className="w-full bg-brand hover:bg-brand/90 text-brand-foreground"
              >
                {submitting
                  ? 'Creating…'
                  : `Create Order${cart.length ? ` · ${totals.qty} units${totals.backorder_qty > 0 ? ` (${totals.backorder_qty} on backorder)` : ''}` : ''}`}
              </Button>
              {!cart.length && (
                <p className="text-[10px] text-muted-foreground italic text-center mt-2">
                  Add at least one stock line to continue.
                </p>
              )}
            </div>
          </div>

          {/* RIGHT — Stock Picker */}
          <div className="p-4 overflow-y-auto bg-muted/30">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Pick stock</h3>
            <div className="relative mb-3">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by SKU code, name, or barcode…"
                data-testid="manual-order-search"
                className="pl-9"
              />
            </div>

            {invLoading ? (
              <div className="text-xs text-muted-foreground italic text-center py-8">Loading inventory…</div>
            ) : inventory.length === 0 ? (
              <div data-testid="manual-order-picker-empty" className="text-xs text-muted-foreground italic text-center py-8 border border-dashed border-border rounded-md">
                No SKUs match "{searchDebounced}".
              </div>
            ) : (
              <div className="space-y-1" data-testid="manual-order-picker">
                {inventory.map((row) => {
                  const oos = row.available <= 0;
                  // Block clicks only when stock is empty AND tenant has
                  // backorders disabled. Otherwise OOS rows are clickable
                  // and the entire qty becomes a backorder line.
                  const blocked = oos && !allowBackorders;
                  return (
                    <button
                      key={row.variant_id}
                      type="button"
                      onClick={() => addToCart(row)}
                      disabled={blocked}
                      data-testid={`manual-order-picker-row-${row.sku_code}-${row.variant_id}`}
                      title={oos
                        ? (blocked
                            ? 'Out of stock — backorders are disabled for this tenant'
                            : 'Out of stock — adding will place this line on backorder')
                        : `${row.available} on hand`}
                      className={`w-full text-left bg-card border rounded-md p-2 flex items-center gap-3 transition-all ${
                        blocked
                          ? 'opacity-50 cursor-not-allowed border-border'
                          : oos
                            ? 'border-amber-300 bg-amber-50/40 hover:border-amber-500 hover:shadow-sm cursor-pointer'
                            : 'border-border hover:border-brand hover:shadow-sm cursor-pointer'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[11px] font-semibold text-foreground truncate">{row.sku_code}</span>
                          {row.is_bundle && (
                            <span className="text-[9px] px-1 py-0.5 rounded bg-purple-100 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 font-semibold">BUNDLE</span>
                          )}
                          {oos && (
                            <span
                              data-testid={`manual-order-picker-backorder-${row.sku_code}`}
                              className={`text-[9px] inline-flex items-center gap-0.5 px-1 py-0.5 font-bold uppercase tracking-wider border ${
                                blocked
                                  ? 'bg-rose-50 text-rose-700 border-rose-200'
                                  : 'bg-amber-100 text-amber-800 border-amber-200'
                              }`}
                            >
                              {blocked ? 'Out of stock' : <><ClockCountdown size={10} weight="bold" /> Backorder</>}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-foreground truncate">{row.sku_name || '—'}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">
                          ${row.unit_price.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-sm font-mono font-bold ${
                          oos ? 'text-rose-600' :
                          row.available < 5 ? 'text-amber-600' : 'text-emerald-600'
                        }`}>
                          {row.available}
                        </div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">avail</div>
                      </div>
                      {!blocked && (
                        <Plus size={14} weight="bold" className="text-muted-foreground shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
